// src/views/UserViews/insights/components/MetricCard.tsx
//
// Shared card for a single Insights metric - the ONLY place percentage formatting happens.
// Callers pass a raw `number | null` ratio (0..1) for `format="percent"`, never a
// pre-formatted string.
//
// Rule 1: a null value means "not enough data" and must render an em dash + "Not enough
// data" copy - never `0`/`0.0%`/"-%". Null and a genuine zero use distinct testids
// (`${testId}-value` vs `${testId}-empty`) so tests can assert the difference directly.
//
// See `MetricCardVariant`'s comment for 'card' vs 'flat', and the "Hover/focus reveal row"
// comment above `REVEAL_ROW_CLASS` below for the whole-card hover/reveal mechanism.
import type { KeyboardEvent } from 'react';
import { Box, Card, CardContent, Skeleton, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '@/resources/strings/StringKeys';
import {
  INSIGHTS_CARD_BORDER_COLOR,
  INSIGHTS_CARD_BORDER_RADIUS,
  INSIGHTS_CARD_BOX_SHADOW,
} from './cardSurfaceStyles';

export type MetricCardFormat = 'count' | 'percent';

/** 'card' (default) is the bordered box; 'flat' drops the `Card`/`CardContent` wrapper for a
 * borderless, section-owned layout while reusing the same loading/error/null/populated
 * branch tree - keeps the null-vs-zero contract (Rule 1) single-sourced instead of forking
 * it into a second component. 'flat' has no whole-card hover surface and keeps the label's
 * own tooltip icon. */
export type MetricCardVariant = 'card' | 'flat';

/** 'start' (default) is the existing left-anchored layout. 'center' centers the label/value
 * content and the row it sits in (so the tooltip icon doesn't end up orphaned on one side
 * while the text centers past it) - a purely additive prop, same shape as `variant`. */
export type MetricCardAlign = 'start' | 'center';

/** Visual-only status dot on the reveal row, left of the merged caption/tooltip text, tinted
 * per `TONE_COLOR`. Never rendered for null/error/loading (Rule 1) - can't imply a health
 * status for "not enough data". */
export type MetricCardTone = 'neutral' | 'positive' | 'warning';

type MetricCardProps = {
  /** Stable id used to build this card's data-testids, so a page with several MetricCards
   * (e.g. FleetStatusSection's four) lets tests target one card unambiguously. */
  testId: string;
  label: string;
  /** Raw value; a ratio in [0, 1] when `format` is "percent". `null` means "not enough
   * data", never a coerced zero (Rule 1) - callers must pass the DAO's null through as-is. */
  value: number | null;
  format: MetricCardFormat;
  /** One-line supplementary detail for a real (non-null) value. Optional - with no caption
   * and no `tooltip`, no hover reveal renders at all. Surfaced via the whole-card reveal row
   * (see that comment above `REVEAL_ROW_CLASS`), never as permanently visible text. */
  caption?: string;
  /** One-line definition normally shown in an info tooltip beside the label - unless this is
   * a populated 'card'-variant instance, where it merges into the same whole-card reveal row
   * `caption` uses (see that comment) instead of staying a second, overlapping hover target. */
  tooltip?: string;
  loading?: boolean;
  error?: Error | null;
  /** Overrides the default "Not enough data" wording for the null state (Task 4.4's
   * coverage-specific "Not enough data for this window"). */
  nullCaption?: string;
  /** Tints the reveal row's tone dot and its text color; default 'neutral'. Never rendered
   * for the null/error/loading branches - see `MetricCardTone`'s own comment. */
  tone?: MetricCardTone;
  /** See `MetricCardVariant`'s own comment. */
  variant?: MetricCardVariant;
  /** See `MetricCardAlign`'s own comment. */
  align?: MetricCardAlign;
  /** Overrides the populated (non-null) value's color - e.g. a caller-computed threshold.
   * Never applied to the null em dash (Rule 1), regardless of this prop. */
  valueColor?: string;
  /** `true` (default) keeps the whole-card hover/focus reveal (see that comment above
   * `REVEAL_ROW_CLASS`). `false` opts out entirely: `caption` renders as a permanent line,
   * `tooltip` goes back to the label's own icon+`Tooltip`, and none of the reveal row's
   * DOM/keyboard machinery is added. */
  revealOnHover?: boolean;
  /** Makes a 'card'-variant instance a whole-card button (pointer, Enter and Space) - e.g.
   * Fleet Status's "Active Now" opening its Active Users dialog. Ignored for 'flat'. Callers
   * should omit it for branches with nothing to drill into (loading/error). */
  onClick?: () => void;
};

const PERCENT_MULTIPLIER = 100;
const PERCENT_DECIMAL_PLACES = 1;
const SKELETON_VALUE_WIDTH = 72;
const EM_DASH = '\u2014';
// px - sized to stay visually proportionate to the reveal text's own 14px
// (REVEAL_TEXT_FONT_SIZE below) rather than shrinking against it.
const TONE_DOT_SIZE = 7;

// Card box metrics, in px. CARD_PADDING is applied locally (not the global MuiCardContent
// override every other card shares). CARD_MIN_HEIGHT is a floor, not fixed - stops a
// loading/error/null card collapsing shorter than a populated sibling in the same row.
// Border color/radius/shadow come from the shared `cardSurfaceStyles` tokens (see that
// module's header) for the same "don't paint via a global theme default" reason.
const CARD_PADDING = 24;
const CARD_MIN_HEIGHT = 116;
const CARD_ROW_GAP = 10;

// Row 1 - label. Matches SectionCard's/FeatureComplianceSection's section-title treatment
// (`variant="subtitle1"` + `fontWeight: 700` - see LABEL_SX below) instead of a bespoke
// uppercase/letter-spaced caption, so a KPI card's label reads consistently with the page.
const LABEL_FONT_WEIGHT = 700;

// Row 2 - value. Set explicitly rather than leaning on the `h4` variant's ~34px scale: this
// now sits just under that variant's scale, but stays an explicit value (not inherited) since
// it's still the visually dominant element on the card and has to read larger than the other
// two rows.
const VALUE_FONT_SIZE = '1.875rem'; // 30px
const VALUE_FONT_WEIGHT = 700;
const VALUE_LINE_HEIGHT = 1.1;

// Row 3 - the null-state caption ("Not enough data").
const CAPTION_FONT_SIZE = '0.8125rem'; // 13px
const CAPTION_FONT_WEIGHT = 500;
const CAPTION_LINE_HEIGHT = 1.5;

// One step LARGER than the null-state caption below (they used to share CAPTION_SX) - 13px
// read too small next to this card's subtitle1 label and 30px value. 14px stays comfortably
// under the label's 16px so the reveal still reads as secondary. The null-state caption
// keeps 13px since it's permanently visible, a different visual job.
const REVEAL_TEXT_FONT_SIZE = '0.875rem'; // 14px
const REVEAL_TEXT_FONT_WEIGHT = 500;
const REVEAL_TEXT_LINE_HEIGHT = 1.5;

// The skeleton stands in for the value row, so it matches that row's rendered line box
// (30px * 1.1) - otherwise a loading card is a different height than a populated one.
const SKELETON_VALUE_HEIGHT = 33;

// Hover/focus reveal row (populated 'card'-variant only): a card with `caption`/`tooltip`
// surfaces that text via a whole-card `:hover`/`:focus-within` opacity fade (`revealText`
// below) instead of a `Tooltip`/`Popper`, so the info never renders in a portal outside the
// card. `tabIndex={0}` + `role="group"` + `aria-label={revealText}` on the `Card` give
// keyboard/screen-reader users the same info non-visually; a `:focus-visible`-only outline
// (WCAG 2.4.7, see FOCUS_OUTLINE_WIDTH below) backs up the fade in case the CSS transition is
// slow or inconsistent.
//
// A label with its own `tooltip` would otherwise show two overlapping hover targets once the
// caption also moves onto the whole card - `revealText` merges both into one row instead, and
// the label only keeps its own icon+`Tooltip` when nothing is already surfaced by the reveal
// (captionless/tooltipless card, 'flat' variant, an empty/loading/error branch, or
// `revealOnHover={false}`).
//
// A stable class name (rather than a nested component/selector) lets the *card's* own `sx`
// drive this row's opacity on `:hover`/`:focus-within`, since the trigger and the animated
// element are siblings - CSS has no "style my parent's sibling" selector.
const REVEAL_ROW_CLASS = 'metric-card-reveal-row';
// No explicit reserved height - an in-flow flex child already occupies its natural content
// height at rest, so `opacity` alone changes on reveal with no layout shift, even when text
// wraps to more lines.
const REVEAL_ROW_TRANSITION = 'opacity 0.2s ease';

// One entry per tone drives both the dot and the reveal text, so the two can't drift apart.
// `neutral` is the only pair that differs: 'grey.400' suits a 6px dot but is too faint for
// body text, so its text falls back to the standard secondary color.
const TONE_COLOR: Record<MetricCardTone, { dot: string; text: string }> = {
  neutral: { dot: 'grey.400', text: 'text.secondary' },
  positive: { dot: 'success.main', text: 'success.main' },
  warning: { dot: 'warning.main', text: 'warning.main' },
};

// Aligns the dot to the FIRST line of (possibly multi-line) reveal text rather than the
// row's vertical center, then nudges to that line's optical center: half the 21px line box
// (14px * 1.5) minus half the 7px dot, minus 1px for cap-height (vs. the full line box).
const TONE_DOT_TOP_OFFSET = 6; // px

// The `:focus-visible`-only fallback outline (WCAG 2.4.7 safety net). `transparent` at
// rest/`:hover`/mouse-`:focus` keeps it invisible for pointer use; only keyboard
// `:focus-visible` promotes it to a visible color.
const FOCUS_OUTLINE_WIDTH = 2; // px
const FOCUS_OUTLINE_OFFSET = 2; // px

// Hover/focus-within shadow shift, independent of the reveal row's own fade - a resting-vs-
// active state exists even if a user never notices the row fade in. Shadow-ONLY: swapping
// `borderColor` too used to read as a harsh outline against this theme's navy `primary.main`.
// Unrelated to the `:focus-visible` outline above (keyboard-only, never fires on hover).
const ACTIVE_BOX_SHADOW = 2; // MUI elevation index, resolved via theme.shadows[2]

// No `textTransform`/`letterSpacing` override - that bespoke small-caps styling is what made
// this label visually inconsistent with the page's other heading-style text (see
// LABEL_FONT_WEIGHT's comment).
const LABEL_SX = {
  fontWeight: LABEL_FONT_WEIGHT,
};

// Shared by the populated value and the null em dash so the two can never render at
// different sizes - a null card has to sit at exactly the same height as a populated
// sibling in the same grid row.
const VALUE_SX = {
  fontSize: VALUE_FONT_SIZE,
  fontWeight: VALUE_FONT_WEIGHT,
  lineHeight: VALUE_LINE_HEIGHT,
  color: 'primary.main',
};

// The null-state caption ("Not enough data") and, for a `revealOnHover={false}` instance, its
// permanent caption line.
const CAPTION_SX = {
  fontSize: CAPTION_FONT_SIZE,
  fontWeight: CAPTION_FONT_WEIGHT,
  lineHeight: CAPTION_LINE_HEIGHT,
};

// One step larger than CAPTION_SX (see REVEAL_TEXT_FONT_SIZE's comment). Kept byte-for-byte
// identical to UtilizationProgressCard's own REVEAL_TEXT_SX so both cards' reveal rows stay
// visually indistinguishable.
const REVEAL_TEXT_SX = {
  fontSize: REVEAL_TEXT_FONT_SIZE,
  fontWeight: REVEAL_TEXT_FONT_WEIGHT,
  lineHeight: REVEAL_TEXT_LINE_HEIGHT,
};

function formatMetricValue(value: number, format: MetricCardFormat): string {
  // `value` is non-null here by construction - the null branch renders an em dash and never
  // reaches this function, so no null can be coerced into "0.0%" (Rule 1).
  if (format === 'percent')
    return `${(value * PERCENT_MULTIPLIER).toFixed(PERCENT_DECIMAL_PLACES)}%`;
  return String(value);
}

export default function MetricCard({
  testId,
  label,
  value,
  format,
  caption,
  tooltip,
  loading = false,
  error = null,
  nullCaption,
  tone = 'neutral',
  variant = 'card',
  valueColor,
  align = 'start',
  revealOnHover = true,
  onClick,
}: MetricCardProps) {
  const { t } = useTranslation();
  const isEmpty = value === null;
  // Only adds `justifyContent: 'center'` to the label/reveal rows - never touches
  // `alignItems`, so the icon/dot keep their vertical alignment regardless of `align`.
  const rowJustifySx = align === 'center' ? { justifyContent: 'center' } : undefined;
  // Gates the whole-card hover reveal below: a card with nothing real to show must never
  // expose a hover target that implies otherwise (Rule 1).
  const isPopulated = !loading && !error && !isEmpty;
  // `valueColor` only overrides the populated branch - the null em dash keeps its own
  // default color (Rule 1) regardless of this prop.
  const populatedValueSx = valueColor ? { ...VALUE_SX, color: valueColor } : VALUE_SX;

  // Only exists for a populated 'card'-variant instance (Rule 1) - 'flat' callers never get
  // it regardless of their own caption/tooltip. Merges `caption` + `tooltip` into one string.
  const revealText =
    revealOnHover && variant === 'card' && isPopulated
      ? caption && tooltip
        ? `${caption}. ${tooltip}`
        : caption ?? tooltip
      : undefined;
  // Keeps its own info-icon tooltip only when nothing is already surfaced by the reveal row
  // above (see that comment) - 'flat' variant, a captionless/tooltipless card, an
  // empty/loading/error branch, or `revealOnHover={false}`.
  const labelTooltipText = revealText ? undefined : tooltip;
  // Only true for a populated card with a caption, when opted out of the reveal row (Rule 1:
  // never for null/loading/error).
  const showPermanentCaption = !revealOnHover && isPopulated && Boolean(caption);

  // One flex column with a single `gap` drives spacing for all four branches (loading/error/
  // null/populated) - hand-tuning a margin per element would drift the moment one branch
  // changes in isolation. Shared verbatim by both variants so Rule 1's contract has exactly
  // one implementation.
  const content = (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ...rowJustifySx }}>
        {/* `component="span"`, not h2/h3 - this label repeats on 7 cards; promoting all to
            headings would add undifferentiated entries to the page's heading outline. */}
        <Typography
          variant="subtitle1"
          component="span"
          sx={LABEL_SX}
        >
          {label}
        </Typography>
        {labelTooltipText && (
          <Tooltip title={labelTooltipText} placement="top" arrow>
            <InfoOutlinedIcon
              aria-label={labelTooltipText}
              sx={{ fontSize: '1rem', color: 'text.secondary' }}
            />
          </Tooltip>
        )}
      </Box>

      {loading && (
        <Skeleton
          variant="text"
          width={SKELETON_VALUE_WIDTH}
          height={SKELETON_VALUE_HEIGHT}
          data-testid={`${testId}-loading`}
        />
      )}

      {!loading && error && (
        <Typography variant="body2" color="error" data-testid={`${testId}-error`}>
          {t(StringKeys.INSIGHTS_SECTION_LOAD_ERROR)}
        </Typography>
      )}

      {!loading && !error && isEmpty && (
        <>
          <Typography variant="h4" component="p" data-testid={`${testId}-empty`} sx={VALUE_SX}>
            {EM_DASH}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={CAPTION_SX}>
            {nullCaption ?? t(StringKeys.INSIGHTS_METRIC_NOT_ENOUGH_DATA)}
          </Typography>
        </>
      )}

      {!loading && !error && !isEmpty && (
        <>
          <Typography
            variant="h4"
            component="p"
            data-testid={`${testId}-value`}
            sx={populatedValueSx}
          >
            {formatMetricValue(value, format)}
          </Typography>
          {showPermanentCaption && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={CAPTION_SX}
              data-testid={`${testId}-caption`}
            >
              {caption}
            </Typography>
          )}
          {revealText && (
            <Box
              className={REVEAL_ROW_CLASS}
              data-testid={`${testId}-reveal-row`}
              // Already exposed via the card's own `aria-label` above - stays out of the
              // accessibility tree so it isn't announced twice.
              aria-hidden="true"
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 0.75,
                opacity: 0,
                transition: REVEAL_ROW_TRANSITION,
                // Passive reveal - never intercepts pointer events meant for the card.
                pointerEvents: 'none',
                ...rowJustifySx,
              }}
            >
              <Box
                data-testid={`${testId}-tone-dot`}
                sx={{
                  width: TONE_DOT_SIZE,
                  height: TONE_DOT_SIZE,
                  borderRadius: '50%',
                  bgcolor: TONE_COLOR[tone].dot,
                  flexShrink: 0,
                  mt: `${TONE_DOT_TOP_OFFSET}px`,
                }}
              />
              <Typography variant="body2" color={TONE_COLOR[tone].text} sx={REVEAL_TEXT_SX}>
                {revealText}
              </Typography>
            </Box>
          )}
        </>
      )}
    </>
  );

  // Only adds `textAlign: 'center'` on top of each variant's base layout - never touches any
  // branch's colors/spacing, so `align` stays a pure text-alignment concern.
  const contentAlignSx = align === 'center' ? { textAlign: 'center' } : undefined;

  // A clickable card is announced as a button, so its accessible name must carry the label
  // too - the reveal text alone (e.g. "Live data") doesn't say what the button opens.
  const cardInteractionProps = onClick
    ? {
        tabIndex: 0,
        role: 'button',
        'aria-label': revealText ? `${label}. ${revealText}` : label,
        onClick,
        onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          onClick();
        },
      }
    : revealText
      ? { tabIndex: 0, role: 'group', 'aria-label': revealText }
      : {};

  if (variant === 'flat') {
    return (
      <Box
        data-testid={testId}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: `${CARD_ROW_GAP}px`,
          ...contentAlignSx,
        }}
      >
        {content}
      </Box>
    );
  }

  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        minHeight: CARD_MIN_HEIGHT,
        borderColor: INSIGHTS_CARD_BORDER_COLOR,
        borderRadius: `${INSIGHTS_CARD_BORDER_RADIUS}px`,
        boxShadow: INSIGHTS_CARD_BOX_SHADOW,
        // Focus-outline safety net only for a `revealOnHover` or clickable instance - otherwise
        // stays a completely plain `Card`.
        ...((revealOnHover || onClick) && {
          outline: `${FOCUS_OUTLINE_WIDTH}px solid transparent`,
          outlineOffset: `${FOCUS_OUTLINE_OFFSET}px`,
          transition: 'box-shadow 0.2s ease',
          '&:focus-visible': { outlineColor: 'primary.main' },
        }),
        ...(onClick && {
          cursor: 'pointer',
          '&:hover': { boxShadow: ACTIVE_BOX_SHADOW },
          '&:focus-within': { boxShadow: ACTIVE_BOX_SHADOW },
        }),
        // Only wired up when there's something to reveal, so a captionless/tooltipless (or
        // null/loading/error) card never grows an inert hover style.
        ...(revealText && {
          [`&:hover .${REVEAL_ROW_CLASS}`]: { opacity: 1 },
          [`&:focus-within .${REVEAL_ROW_CLASS}`]: { opacity: 1 },
          '&:hover': { boxShadow: ACTIVE_BOX_SHADOW },
          '&:focus-within': { boxShadow: ACTIVE_BOX_SHADOW },
        }),
      }}
      data-testid={testId}
      // Only a card actually revealing something (or clickable) gets keyboard-focus/aria
      // treatment.
      {...cardInteractionProps}
    >
      <CardContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: `${CARD_ROW_GAP}px`,
          p: `${CARD_PADDING}px`,
          '&:last-child': { pb: `${CARD_PADDING}px` },
          ...contentAlignSx,
        }}
      >
        {content}
      </CardContent>
    </Card>
  );
}
